import {
    Component,
    Optional,
    ViewEncapsulation,
    OnDestroy,
    ChangeDetectorRef,
    HostListener,
    OnInit
} from '@angular/core';
import { Location } from '@angular/common';
import { AuthState } from 'app/services/auth-state';
import { Panel } from 'app/utils/panel';
import { ReputationService } from 'app/services/reputation';
import { LicenseState } from 'app/services/license-state';
import { RequestsState } from 'app/services/requests-state';
import { EulaService } from 'app/components/eula/eula.service';
import { I360Config } from 'app/utils/config/config';
import { AppState } from 'app/core/app.service';
import { fromEvent, Subject, bufferCount, filter, map, takeUntil, firstValueFrom, noop } from 'rxjs';
import { I360ConfigDataItems } from '@imunify360-api/settings';
import { EmailsService } from 'app/services/emails';
import { AuthService, I360Role } from "app/services/auth";
import { Product, Package } from '@imunify360-api/license';
import { UserFeaturesStatusService } from 'app/services/user-features-status';
import { NavigationEnd, Router } from '@angular/router';
import { AgentStatus } from '@imunify360-api/misc';
import { MyImunifyService } from 'app/services/my-imunify';
import { IMyImunifyState } from 'app/services/my-imunify-state';
import { FirewallService } from 'app/components/lists/firewall.service';
import { InstallationBackendService } from 'app/components/installation/installation-backend.service';
import { LicenseService } from 'app/services/license';
import { SettingsService } from 'app/services/settings';
import { NotificationsService } from 'app/services/notifications';
import { MyImunifyPromoDialogService } from 'app/services/my-imunify-promo-dialog/my-imunify-promo-dialog.service';

declare const i360PleskSettings: {
    buyUrl: string,
    marketplace: boolean,
    licenseType: string,
};

export const PAYMENT_SUCCESS_DELAY_MS = 1500;

@Component({
    selector: 'nav-root',
    templateUrl: './nav-root.component.html',
    providers: [
        I360Config,
    ],
    styleUrls: ['./nav-root.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class NavRootComponent implements OnDestroy, OnInit {
    support = this.config.changes.pipe(
        map((c: I360ConfigDataItems) =>  {
            return c.PERMISSIONS.support_form
        }),
    );
    imunifyPackage = IMUNIFY_PACKAGE;
    AgentStatus = AgentStatus;
    Product = Product;
    userRole: I360Role = this.authState.role.value;
    isClient: boolean;
    isAdmin: boolean;
    myImunifyState: IMyImunifyState;

    // Listener to catch the payment event and handle it
    @HostListener('window:message', ['$event']) onPayout(event) {
        this.myImunifyPromoDialog.handlePayoutEvent(event, this.myImunifyState);
    }

    private destroy$ = new Subject<void>();

    constructor(
        private cdr: ChangeDetectorRef,
        public panel: Panel,
        public firewallService: FirewallService,
        public emailsService: EmailsService,
        public authState: AuthState,
        public authService: AuthService,
        public appState: AppState,
        public licenseState: LicenseState,
        public licenseService: LicenseService,
        public requestsState: RequestsState,
        @Optional() public eulaService: EulaService,
        // looks like component level providers are lazy loaded
        @Optional() public reputationService: ReputationService,
        public config: I360Config,
        userFeaturesStatusService: UserFeaturesStatusService,
        private router: Router,
        private location: Location,
        public myImunifyService: MyImunifyService,
        public myImunifyPromoDialog: MyImunifyPromoDialogService,
        private installationBackendService: InstallationBackendService,
        private settingsService: SettingsService,
        private notifications: NotificationsService,
    ) {
        if (authState.role.value === I360Role.none) {
            authService.goToLoginPage();
        }

        // Need for login
        this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.userRole = this.authState.role.value;
            }
        })

        //  Need for iframe in DirectAdmin panel
        if (this.panel.isDirectAdmin && document.querySelector('#iframe-container')) {
            fromEvent(window, 'mousemove').pipe(
                map((event: MouseEvent) => event.clientY),
                bufferCount(20, 1),
                filter((clientY) => {
                    return ((this.appState.mousePositionY + 30  < clientY[0]) ||
                        (this.appState.mousePositionY - 30 > clientY[0]));
                }),
                takeUntil(this.destroy$),
            ).subscribe((position) => {
                this.appState.mousePositionY = position[0];
            });
        }

        this.authState.isClient.pipe(takeUntil(this.destroy$)).subscribe(isClient => {
            this.isClient = isClient;
            this.cdr.markForCheck();
        });
        this.authState.isAdmin.pipe(takeUntil(this.destroy$)).subscribe(isAdmin => {
            this.isAdmin = isAdmin;
            this.cdr.markForCheck();
        });
        this.authState.role.pipe(takeUntil(this.destroy$)).subscribe(authRole => {
            this.userRole = authRole;
            this.cdr.markForCheck();
        });
        if (this.imunifyPackage === Package.imunify360) {
            this.listenMyImunifyState();
        }
    }

    get isVisibleMyImunifyUpgradeBtn(): boolean {
        return this.isClient && this.myImunifyState?.enabled && !this.myImunifyState.protection;
    }

    ngOnInit() {
        if (this.panel.isPlesk && this.isAdmin) {
            this.checkInstallationStatus();
            this.checkAndRetrieveThePleskLicense();
        }
    }

    private listenMyImunifyState(): void {
        this.myImunifyService.getMyImunifyState()
            .pipe(takeUntil(this.destroy$))
            .subscribe(state => {
                this.myImunifyState = state;
                if (this.isVisibleMyImunifyUpgradeBtn) {
                    this.myImunifyPromoDialog.checkNeedToShowMyImunifyUpgradeDialog(state.username as string);
                }
                this.cdr.markForCheck();
            });
    }

    private checkInstallationStatus(): void {
        // If we are in the installation process - interceptor should redirect to installation page
        this.installationBackendService.status({ offset: 0, limit: 1 }, false, false)
            .pipe(takeUntil(this.destroy$))
            .subscribe({ next: noop, error: noop })
    }

    isActive(tab: string): boolean {
        const destinationPath = [this.imunifyPackage, this.userRole, tab].join('/');
        return this.location.path().includes(destinationPath);
    }

    navigateToTab(tab: string): void {
        const command = tab ? [this.imunifyPackage, this.userRole, tab] : [''];

        this.router.navigate(command);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    // Retrieve the license if the license type in plesk settings does not match the license type in agent response
    checkAndRetrieveThePleskLicense() {
        this.licenseState.license.subscribe(async (data) => {
            if (typeof i360PleskSettings !== 'undefined' && i360PleskSettings) {
                if (!data?.license_type || !i360PleskSettings.licenseType) return;
                const fromMarketPlace = i360PleskSettings.marketplace;
                const licenseTypeFromPlesk = fromMarketPlace
                    ? i360PleskSettings.licenseType : '';
                const licenseTypeFromAgent = data.license_type;
                const update = fromMarketPlace && licenseTypeFromPlesk !== licenseTypeFromAgent;
                if (update) {
                    await firstValueFrom(this.licenseService.registerViaPanel(), { defaultValue: undefined });
                }
            }
        });
        // Notify customer about missed license for Imunify360
        this.settingsService.packageVersions.subscribe(data => {
            const packages = data?.packages || '';
            if (packages.includes('imunify360-firewall') && IMUNIFY_PACKAGE === Package.imunifyAV) {
                this.notifications.warning('license.noValidLicenseForImunify360Installation');
            }
        })
    }
}
